import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import { getAppSettingsData, type AppSettingsNormalized } from "~/utils/receptionDB";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import { CallType, NotificationType, ScreenType } from "~/utils/receptionDB.types";
import type {
  ChatCompleteNavigationState,
  ChatRequestType,
  ReceptionByAppointmentGroupName,
  ReceptionByAppointmentName,
  RingUpPhoneNavigationState,
} from "~/utils/receptionDB.types";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import ReceptionHeader from "~/components/ReceptionHeader";
import ReceptionKeypadButton from "~/components/ReceptionKeypadButton";
import * as icon from "~/Icons";

import { languageAtom, receptionAtom, receptionCompanyIdAtom } from "~/Atom";

import { useReceptionByAppointment } from "~/components/hooks/useReceptionByAppointment";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import type { ReceptionByAppointmentData } from "~/components/hooks/useReceptionByAppointment";

import { mapErrorCode, buildErrorPath } from "~/utils/error";
import { runReceptionBack } from "~/utils/receptionNavigation";
import { ROUTE } from "~/constants/routes";

import "~/css/reception/common.css";
import "~/css/reception/ReceptionNumericInputPage.css";

const MAX_LENGTH = 5;
const DEFAULT_ERROR_CODE = "N9999";
const INVALID_CALL_TYPE = "N9996";
const INVALID_CHAT_STATUS = "N9997";
const NO_CALL_DESTINATION = "N2009";
const FACETIME_PHONEBOOK_CONFLICT = "N2010";

const normalizeValue = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim().toUpperCase();
};

const toChatRequestType = (isChatSuccess: boolean): ChatRequestType => {
  return isChatSuccess ? "success" : "failed";
};

const resolveLocalizedAppointmentText = <T extends { languageCode?: string }>(
  items: T[] | null | undefined,
  languageCode: string,
  getText: (item: T) => string | null | undefined
): string | undefined => {
  if (!items?.length) return undefined;

  const normalizedLanguage = languageCode.toLowerCase();
  const localizedItem =
    items.find((item) => normalizedLanguage.startsWith(item.languageCode?.toLowerCase() ?? "")) ??
    items[0];
  const text = getText(localizedItem)?.trim();
  return text ? text : undefined;
};
/**
 * 受付番号の入力、送信、エラー遷移を扱う受付番号入力画面。
 */
const InputReceptionCodePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [value, setValue] = useState<string>("");

  const { submit, isLoading } = useReceptionByAppointment();

  const [language] = useAtom(languageAtom);
  const [, setReceptionData] = useAtom(receptionAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  const appSettingsRef = useRef<AppSettingsNormalized | null>(null);
  const appSettingsLoadedRef = useRef(false);
  const appSettingsRequestRef = useRef<Promise<AppSettingsNormalized | undefined> | null>(null);

  const getCachedAppSettings = useCallback(async (): Promise<AppSettingsNormalized | undefined> => {
    if (appSettingsLoadedRef.current) {
      return appSettingsRef.current ?? undefined;
    }
    if (appSettingsRequestRef.current) {
      return appSettingsRequestRef.current;
    }

    appSettingsRequestRef.current = getAppSettingsData()
      .then((settings) => {
        appSettingsRef.current = settings ?? null;
        appSettingsLoadedRef.current = true;
        return settings;
      })
      .finally(() => {
        appSettingsRequestRef.current = null;
      });

    return appSettingsRequestRef.current;
  }, []);

  const resolveIsPhoneBookEnabled = useCallback(async (): Promise<boolean> => {
    const appSettings = await getCachedAppSettings();
    const basics = appSettings?.basics ?? [];
    const groupDisplayId = receptionCompanyId;
    const basic =
      (groupDisplayId !== undefined
        ? basics.find((item) => item.id === groupDisplayId)
        : undefined) ?? basics[0];

    return !!basic?.isPhoneBookEnabled;
  }, [getCachedAppSettings, receptionCompanyId]);

  /**
   * 対応する音声エンジンが利用可能な場合のみ、初回表示時に受付番号入力の案内を一度だけ読み上げる。
   */
  useEffect(() => {
    void playWithCooldown(t("titleInputReceptionCode")).catch((error) => {
      console.error("inputReceptionCode playVoice failed:", error);
    });
  }, [playWithCooldown]);

  /**
   * 一定時間操作がない場合は、設定値に従って受付開始画面へ戻す。
   */
  useInactivityTimeout(navigate);

  /**
   * 数字ボタン押下時に、最大桁数まで受付番号を追加入力する。
   */
  const handleNumberClick = (num: string) => {
    setValue((prev) => (prev.length >= MAX_LENGTH ? prev : prev + num));
  };

  const handleDelete = () => {
    setValue((prev) => prev.slice(0, -1));
  };
  const navigateForward = useCallback(
    (path: string, state?: RingUpPhoneNavigationState | ChatCompleteNavigationState) => {
      navigate(path, { state });
    },
    [navigate]
  );

  const navigateCallProcess = useCallback(
    (data: ReceptionByAppointmentData, chatRequestType: ChatRequestType) => {
      const state: RingUpPhoneNavigationState = {
        groupName: resolveLocalizedAppointmentText(
          data.groupNames as ReceptionByAppointmentGroupName[] | undefined,
          language ?? "",
          (item) => item.groupName
        ),
        userName: resolveLocalizedAppointmentText(
          data.names as ReceptionByAppointmentName[] | null | undefined,
          language ?? "",
          (item) => item.name
        ),
        chatRequestType,
        callType: data.callType ?? undefined,
        extensionNumber: data.extensionNumber ?? undefined,
        faceTimeAddress: data.faceTimeAddress ?? undefined,
        appointmentId: data.appointmentId,
        appointmentVisitorIds: data.appointmentVisitorIds,
      };
      navigateForward(ROUTE.RECEPTION.CALLING, state);
    },
    [language, navigateForward]
  );

  const validateCallProcess = useCallback(
    (data: ReceptionByAppointmentData, isPhoneBookEnabled: boolean): boolean => {
      const callType = normalizeValue(data.callType);

      if (!callType) {
        navigate(buildErrorPath(INVALID_CALL_TYPE));
        return false;
      }

      if (callType === CallType.Phone) {
        if (!data.extensionNumber?.trim()) {
          navigate(buildErrorPath(NO_CALL_DESTINATION));
          return false;
        }
        return true;
      }

      if (callType === CallType.FaceTime) {
        if (isPhoneBookEnabled) {
          navigate(buildErrorPath(FACETIME_PHONEBOOK_CONFLICT));
          return false;
        }
        if (!data.faceTimeAddress?.trim()) {
          navigate(buildErrorPath(NO_CALL_DESTINATION));
          return false;
        }
        return true;
      }

      navigate(buildErrorPath(INVALID_CALL_TYPE));
      return false;
    },
    [navigate]
  );

  const isValid = value.length === 5;
  const deleteLabel = t("delete");
  const okLabel = t("ok");

  /**
   * 受付登録結果の通知種別に応じて、完了画面、通話画面、またはエラー画面へ遷移する。
   */
  const handleNavigation = useCallback(
    (data: ReceptionByAppointmentData, isPhoneBookEnabled: boolean) => {
      switch (data.notificationType) {
        case NotificationType.Chat:
          navigateForward(ROUTE.RECEPTION.COMPLETE, {
            groupName: resolveLocalizedAppointmentText(
              data.groupNames as ReceptionByAppointmentGroupName[] | undefined,
              language ?? "",
              (item) => item.groupName
            ),
            userName: resolveLocalizedAppointmentText(
              data.names as ReceptionByAppointmentName[] | null | undefined,
              language ?? "",
              (item) => item.name
            ),
          });
          return;

        case NotificationType.Call:
          if (!validateCallProcess(data, isPhoneBookEnabled)) {
            return;
          }
          navigateCallProcess(data, "noRequest");
          return;

        case NotificationType.ChatAndCall:
          if (data.isChatSuccess === undefined || data.isChatSuccess === null) {
            navigate(buildErrorPath(INVALID_CHAT_STATUS));
            return;
          }
          if (!validateCallProcess(data, isPhoneBookEnabled)) {
            return;
          }
          navigateCallProcess(data, toChatRequestType(data.isChatSuccess));
          return;

        default:
          navigate(buildErrorPath(DEFAULT_ERROR_CODE));
      }
    },
    [language, navigate, navigateCallProcess, navigateForward, validateCallProcess]
  );

  /**
   * 入力された受付番号を送信し、成功時は受付情報を保存して次画面へ進む。
   * 失敗時はバックエンドエラーコードをUI用コードへ変換してエラー画面へ遷移する。
   */
  const handleSubmit = async () => {
    if (isLoading) return;

    if (!isValid) {
      navigate(buildErrorPath(DEFAULT_ERROR_CODE));
      return;
    }

    // If the entered reception number starts with 0, navigate to N2003 error page
    if (value.startsWith("0")) {
      navigate(buildErrorPath("N2003"));
      return;
    }

    try {
      const data = await submit({
        receptionNumber: Number(value),
      });
      const isPhoneBookEnabled = await resolveIsPhoneBookEnabled();

      setReceptionData(data);

      handleNavigation(data, isPhoneBookEnabled);
    } catch (err: unknown) {
      const maybeErr = err as { errorCode?: unknown } | undefined;
      const rawCode = maybeErr?.errorCode;

      const backendCode = typeof rawCode === "number" ? rawCode : undefined;

      const uiCode = mapErrorCode(ScreenType.RECEPTION_CODE_INPUT, backendCode);

      navigate(buildErrorPath(uiCode));
    }
  };

  const handleBack = useCallback(() => {
    runReceptionBack({ kind: "router-history", navigate });
  }, [navigate]);

  return (
    <div className="reception-code-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />

      <main className="reception-code-screen">
        <div className="screen__text reception-code-screen__text">
          <h2 className="screen__title">{t("titleInputReceptionCode")}</h2>
        </div>

        <div className="reception-code-screen__body">
          <div className="reception-code-display-box" aria-live="polite">
            {value.length === 0 ? (
              <span className="reception-code-display-box__placeholder">
                {t("placeholderReceptionCode")}
              </span>
            ) : (
              <span className="reception-code-display-box__value">{value}</span>
            )}
          </div>

          <div className="reception-code-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <ReceptionKeypadButton
                key={n}
                ariaLabel={String(n)}
                label={String(n)}
                onClick={() => handleNumberClick(String(n))}
              />
            ))}

            <ReceptionKeypadButton ariaLabel="0" label="0" onClick={() => handleNumberClick("0")} />

            <ReceptionKeypadButton ariaLabel={deleteLabel} onClick={handleDelete}>
              <icon.icon_delete className="reception-keypad-button__icon" aria-hidden="true" />
            </ReceptionKeypadButton>

            <ReceptionKeypadButton
              ariaLabel={okLabel}
              disabled={!isValid || isLoading}
              label={okLabel}
              onClick={() => {
                void handleSubmit();
              }}
              variant="primary"
            />
          </div>

          {isLoading && <ReceptionCommunicatingIndicator />}
        </div>
      </main>
    </div>
  );
};

export default InputReceptionCodePage;
